import { createContext, useCallback, useRef, useState } from 'react'

import { Toast } from './Toast'
import type {
  ToastContextType,
  ToastData,
  ToastProviderProps,
} from './ToastProvider.types'

const EXIT_ANIMATION_MS = 200

export const ToastContext = createContext<ToastContextType | null>(null)

export const ToastProvider = (props: ToastProviderProps) => {
  const { children } = props
  const [toast, setToast] = useState<ToastData | null>(null)
  const [toastKey, setToastKey] = useState(0)
  const [isExiting, setIsExiting] = useState(false)
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearExitTimer = useCallback(() => {
    if (exitTimerRef.current) {
      clearTimeout(exitTimerRef.current)
      exitTimerRef.current = null
    }
  }, [])

  const showToast = useCallback(
    (data: ToastData) => {
      clearExitTimer()
      setIsExiting(false)
      setToast(data)
      setToastKey(k => k + 1)
    },
    [clearExitTimer],
  )

  const hideToast = useCallback(() => {
    setIsExiting(true)
    exitTimerRef.current = setTimeout(() => {
      setToast(null)
      setIsExiting(false)
    }, EXIT_ANIMATION_MS)
  }, [])

  return (
    <ToastContext.Provider value={{ hideToast, showToast }}>
      {children}

      {toast && (
        <div
          className={`ToastContainer${isExiting ? ' ToastContainer--exiting' : ''}`}
        >
          <Toast
            key={toastKey}
            autoClose={toast.autoClose}
            content={toast.content}
            hideCloseButton={toast.hideCloseButton}
            onClose={hideToast}
            variant={toast.variant}
          />
        </div>
      )}
    </ToastContext.Provider>
  )
}
